import { useState } from "react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { Link } from "react-router";
import { useMutation } from "@apollo/client/react";
import { ImportPersonAction, ImportUnitErrorCode, ImportWarningCode } from "@/graphql/generated/types";
import {
  DeletePersonDocument,
  DeleteSharedItemDocument,
  type ImportUnitFieldsFragment,
  type ImportPersonActionFieldsFragment,
  type ImportWarningFieldsFragment,
} from "@/graphql/generated/operations";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/design/Button";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import { useMe } from "@/auth/use-me";
import { userErrorMessage } from "@/lib/user-error";
import { partitionImportUnits } from "./import-job-snapshots";
import { ImportProblemGuidance } from "./ImportProblemGuidance";

/**
 * Per-unit outcome and non-fatal warnings of a finished job.
 *
 * `ImportUnitResult.message` is deliberately NOT selected in the GraphQL
 * fragment and therefore cannot be rendered: it carries genesis-internal
 * persistence/parser text. The localized `errorCode` is what the operator sees
 * (@adr SPARK-0010-no-raw-server-errors-in-ui@1); the unit `id` and the job id remain the support codes.
 */

const ERROR_CODE_LABELS: Record<ImportUnitErrorCode, { id: string; defaultMessage: string }> = {
  [ImportUnitErrorCode.InvalidInput]: {
    id: "service.page.bulkYaml.unitInvalidInput",
    defaultMessage: "The document was rejected: invalid or missing values.",
  },
  [ImportUnitErrorCode.InvalidReference]: {
    id: "service.page.bulkYaml.unitInvalidReference",
    defaultMessage: "A reference in the document does not exist.",
  },
  [ImportUnitErrorCode.Duplicate]: {
    id: "service.page.bulkYaml.unitDuplicate",
    defaultMessage:
      "This entry already exists. Use “merge” instead of “insert” to update it, or remove it first.",
  },
  [ImportUnitErrorCode.PersistenceError]: {
    id: "service.page.bulkYaml.unitPersistenceError",
    defaultMessage: "The database refused this write.",
  },
  [ImportUnitErrorCode.DependencyFailed]: {
    id: "service.page.bulkYaml.unitDependencyFailed",
    defaultMessage: "Not attempted, because a unit it depends on did not succeed.",
  },
  [ImportUnitErrorCode.UnexpectedError]: {
    id: "service.page.bulkYaml.unitUnexpectedError",
    defaultMessage: "Unexpected error while importing this unit.",
  },
};

/**
 * Where a successfully imported document can be looked at. Only kinds with a
 * detail route of their own appear here: everything else stays plain text
 * rather than linking somewhere that merely exists.
 */
const DETAIL_ROUTE: Record<string, (id: string) => string> = {
  Person: (id) => `/p/${id}`,
  SharedItem: (id) => `/share/discover/${id}`,
};

function unitHref(unit: ImportUnitFieldsFragment): string | null {
  // genesis withholds entityId on failures, skips and every dry run, so an
  // absent key is the normal case here and not an error worth reporting.
  if (!unit.entityId) return null;
  return DETAIL_ROUTE[unit.kind]?.(unit.entityId) ?? null;
}

function unitTechnicalId(unit: ImportUnitFieldsFragment): string {
  if (unit.entityId) return unit.entityId;
  const kindPrefix = `${unit.kind}/`;
  return unit.id.startsWith(kindPrefix) ? unit.id.slice(kindPrefix.length) : unit.id;
}

/**
 * Kinds an operator can delete again straight from the outcome list, wired to
 * the domain's own delete mutations. The dialog copy must name the blast
 * radius: on a merge import the entity may predate the import, so deleting
 * removes the whole aggregate, never just the imported changes.
 */
const DELETABLE_KINDS = new Set(["Person", "SharedItem"]);

const DELETE_DIALOG_COPY: Record<
  string,
  { title: { id: string; defaultMessage: string }; message: { id: string; defaultMessage: string } }
> = {
  Person: {
    title: {
      id: "service.page.bulkYaml.unitDeletePersonTitle",
      defaultMessage: "Delete person?",
    },
    message: {
      id: "service.page.bulkYaml.unitDeletePersonMessage",
      defaultMessage:
        "“{id}” will be deleted permanently, including all profile data. This removes the whole person, not just the imported changes.",
    },
  },
  SharedItem: {
    title: {
      id: "service.page.bulkYaml.unitDeleteSharedItemTitle",
      defaultMessage: "Delete post?",
    },
    message: {
      id: "service.page.bulkYaml.unitDeleteSharedItemMessage",
      defaultMessage:
        "“{id}” will be deleted permanently, including all comments. This removes the whole post, not just the imported changes.",
    },
  },
};

interface UnitDeletion {
  canDelete: (unit: ImportUnitFieldsFragment) => boolean;
  deletedIds: ReadonlySet<string>;
  error: { entityId: string; message: string } | null;
  onRequestDelete: (unit: ImportUnitFieldsFragment) => void;
}

function UnitGroup({
  idPrefix,
  units,
  label,
  tone,
  intl,
  deletion,
  onOpenDocumentation,
  actionByPosition,
  dryRun,
}: {
  idPrefix: string;
  units: readonly ImportUnitFieldsFragment[];
  label: string;
  tone: "ok" | "bad" | "muted";
  intl: IntlShape;
  deletion?: UnitDeletion;
  onOpenDocumentation: (anchor: string) => void;
  actionByPosition: ReadonlyMap<number, ImportPersonAction>;
  dryRun: boolean;
}) {
  if (units.length === 0) return null;
  const dot = tone === "ok" ? "bg-accent" : tone === "bad" ? "bg-red-600" : "bg-ink/30";

  return (
    <div role="group" aria-label={label} className="mt-2">
      <div className="text-smycles-label font-medium text-ink-muted">{label}</div>
      <ul className="mt-1 flex flex-col gap-1">
        {units.map((unit) => {
          const isDeleted = unit.entityId != null && deletion?.deletedIds.has(unit.entityId) === true;
          const href = isDeleted ? null : unitHref(unit);
          const deleteError =
            deletion?.error && deletion.error.entityId === unit.entityId ? deletion.error.message : null;
          return (
            <li key={unit.position} id={`${idPrefix}-unit-${unit.position}`}>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span aria-hidden="true" className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
                {href ? (
                  <Link id={`${idPrefix}-unitlink-${unit.position}`} to={href} className="text-smycles-meta text-ink-accent underline">
                    {unit.label}
                  </Link>
                ) : (
                  <span id={`${idPrefix}-unitlabel-${unit.position}`} className={`text-smycles-meta ${isDeleted ? "text-ink-muted line-through" : "text-ink"}`}>
                    {unit.label}
                  </span>
                )}
                <InfoTooltip
                  ariaLabel={intl.formatMessage(
                    {
                      id: "service.page.bulkYaml.unitTechnicalIdAria",
                      defaultMessage: "Show technical ID for {label}",
                    },
                    { label: unit.label },
                  )}
                  title={intl.formatMessage({
                    id: "service.page.bulkYaml.unitTechnicalId",
                    defaultMessage: "Technical ID",
                  })}
                  placement="right"
                >
                  <span className="font-mono">{unitTechnicalId(unit)}</span>
                </InfoTooltip>
                <span className="text-smycles-label text-ink-muted">{unit.kind}</span>
                {unit.kind === "Person" && actionByPosition.has(unit.position) ? (
                  <span className="rounded bg-accent/[0.08] px-1.5 py-0.5 text-smycles-label text-ink-accent">
                    {actionByPosition.get(unit.position) === ImportPersonAction.Create ? (
                      <FormattedMessage
                        id={dryRun ? "service.page.bulkYaml.personWillBeCreated" : "service.page.bulkYaml.personCreated"}
                        defaultMessage={dryRun ? "New profile will be created" : "New profile created"}
                      />
                    ) : actionByPosition.get(unit.position) === ImportPersonAction.Update ? (
                      <FormattedMessage
                        id={dryRun ? "service.page.bulkYaml.personWillBeUpdated" : "service.page.bulkYaml.personUpdated"}
                        defaultMessage={dryRun ? "Existing profile will be updated" : "Existing profile updated"}
                      />
                    ) : actionByPosition.get(unit.position) === ImportPersonAction.Restore ? (
                      <FormattedMessage id="service.page.bulkYaml.personRestored" defaultMessage="Deleted profile restored" />
                    ) : (
                      <FormattedMessage id="service.page.bulkYaml.personDeleted" defaultMessage="Profile deleted" />
                    )}
                  </span>
                ) : null}
                {unit.errorCode && !unit.problem ? (
                  <span className="text-smycles-label text-ink">
                    {intl.formatMessage(ERROR_CODE_LABELS[unit.errorCode])}
                  </span>
                ) : null}
                {isDeleted ? (
                  <span className="text-smycles-label text-ink-muted">
                    <FormattedMessage id="service.page.bulkYaml.unitDeleted" defaultMessage="Deleted" />
                  </span>
                ) : deletion && deletion.canDelete(unit) ? (
                  <Button
                    id={`${idPrefix}-unitdelete-${unit.position}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => deletion.onRequestDelete(unit)}
                    aria-label={intl.formatMessage(
                      { id: "service.page.bulkYaml.unitDeleteAria", defaultMessage: "Delete {id}" },
                      { id: unit.label },
                    )}
                    className="ml-auto"
                  >
                    <FormattedMessage id="common.delete" defaultMessage="Delete" />
                  </Button>
                ) : null}
              </div>
              {deleteError ? (
                <div className="ml-3.5 text-smycles-label text-red-600">{deleteError}</div>
              ) : null}
              {unit.problem ? (
                <div className="ml-3.5">
                  <ImportProblemGuidance
                    problem={unit.problem}
                    onOpenDocumentation={onOpenDocumentation}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function ImportUnitBreakdown({
  units,
  idPrefix,
  succeededUnits,
  failedUnits,
  skippedUnits,
  totalUnits,
  onOpenDocumentation,
  personActions,
  dryRun,
}: {
  units: readonly ImportUnitFieldsFragment[];
  /**
   * Anker-Präfix. VOM AUFRUFER: dieser Kasten steht je Datei EINMAL, und der
   * Aufklapper hält geschlossene Kästen im DOM. Bei zwei fertigen Dateien gäbe
   * es `import-unit-1` zweimal, weil die Position je Datei neu zählt.
   */
  idPrefix: string;
  succeededUnits: number;
  failedUnits: number;
  skippedUnits: number;
  totalUnits: number;
  onOpenDocumentation: (anchor: string) => void;
  personActions: readonly ImportPersonActionFieldsFragment[];
  dryRun: boolean;
}) {
  const intl = useIntl();
  const me = useMe();
  const [deletePerson] = useMutation(DeletePersonDocument);
  const [deleteSharedItem] = useMutation(DeleteSharedItemDocument);
  const [pendingDelete, setPendingDelete] = useState<ImportUnitFieldsFragment | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deletedIds, setDeletedIds] = useState<ReadonlySet<string>>(new Set());
  const [deleteError, setDeleteError] = useState<{ entityId: string; message: string } | null>(null);

  if (totalUnits === 0) return null;
  const { succeeded, failed, skipped } = partitionImportUnits(units);
  const actionByPosition = new Map<number, ImportPersonAction>(
    personActions.map((action) => [action.documentPosition, action.action]),
  );

  const deletion: UnitDeletion = {
    // The operator's own person never gets a delete affordance: a merge import
    // can legitimately update the account that is running the import.
    canDelete: (unit) =>
      unit.entityId != null && DELETABLE_KINDS.has(unit.kind) && unit.entityId !== me.id,
    deletedIds,
    error: deleteError,
    onRequestDelete: (unit) => {
      setDeleteError(null);
      setPendingDelete(unit);
    },
  };

  const confirmDelete = async () => {
    if (deleteBusy || !pendingDelete?.entityId) return;
    const { kind, entityId } = pendingDelete;
    setDeleteBusy(true);
    try {
      const variables = { input: { id: entityId } };
      if (kind === "Person") {
        await deletePerson({ variables });
      } else {
        await deleteSharedItem({ variables });
      }
      setDeletedIds((prev) => new Set(prev).add(entityId));
    } catch (error) {
      setDeleteError({ entityId, message: userErrorMessage(intl, error) });
    } finally {
      setDeleteBusy(false);
      setPendingDelete(null);
    }
  };

  const dialogCopy = pendingDelete ? DELETE_DIALOG_COPY[pendingDelete.kind] : null;

  return (
    <section className="mt-3 border-t border-ink/10 pt-3">
      {/* Counters come from the job, not from the list: genesis keeps them
          current while a job runs and only fills `units` once it is terminal. */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-smycles-meta">
        <span className="text-ink-accent">
          <FormattedMessage
            id="service.page.bulkYaml.unitsSucceeded"
            defaultMessage="{n} succeeded"
            values={{ n: succeededUnits }}
          />
        </span>
        {failedUnits > 0 ? (
          <span className="text-red-600">
            <FormattedMessage
              id="service.page.bulkYaml.unitsFailed"
              defaultMessage="{n} failed"
              values={{ n: failedUnits }}
            />
          </span>
        ) : null}
        {skippedUnits > 0 ? (
          <span className="text-ink-muted">
            <FormattedMessage
              id="service.page.bulkYaml.unitsSkipped"
              defaultMessage="{n} skipped"
              values={{ n: skippedUnits }}
            />
          </span>
        ) : null}
      </div>

      <UnitGroup
        idPrefix={idPrefix}
        units={succeeded}
        tone="ok"
        intl={intl}
        deletion={deletion}
        onOpenDocumentation={onOpenDocumentation}
        actionByPosition={actionByPosition}
        dryRun={dryRun}
        label={intl.formatMessage({
          id: "service.page.bulkYaml.unitsSucceededGroup",
          defaultMessage: "Succeeded units",
        })}
      />
      <UnitGroup
        idPrefix={idPrefix}
        units={failed}
        tone="bad"
        intl={intl}
        onOpenDocumentation={onOpenDocumentation}
        actionByPosition={actionByPosition}
        dryRun={dryRun}
        label={intl.formatMessage({
          id: "service.page.bulkYaml.unitsFailedGroup",
          defaultMessage: "Failed units",
        })}
      />
      <UnitGroup
        idPrefix={idPrefix}
        units={skipped}
        tone="muted"
        intl={intl}
        onOpenDocumentation={onOpenDocumentation}
        actionByPosition={actionByPosition}
        dryRun={dryRun}
        label={intl.formatMessage({
          id: "service.page.bulkYaml.unitsSkippedGroup",
          defaultMessage: "Skipped units",
        })}
      />

      {pendingDelete && dialogCopy ? (
        <ConfirmDialog
          id={`${idPrefix}-deletedialog`}
          open
          title={intl.formatMessage(dialogCopy.title)}
          message={intl.formatMessage(dialogCopy.message, { id: pendingDelete.label })}
          confirmLabel={intl.formatMessage({ id: "common.delete", defaultMessage: "Delete" })}
          confirmTestId="import-unit-delete-confirm"
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      ) : null}
    </section>
  );
}

export function ImportWarnings({ warnings }: { warnings: readonly ImportWarningFieldsFragment[] }) {
  if (warnings.length === 0) return null;

  return (
    <section className="mt-3 rounded-md bg-attention/[0.07] p-3 ring-1 ring-attention/30">
      {warnings.map((warning) => (
        <div key={warning.code}>
          <div className="text-smycles-meta font-medium text-ink-attention">
            {warning.code === ImportWarningCode.BlobCleanupIncomplete ? (
              <FormattedMessage
                id="service.page.bulkYaml.warningBlobCleanup"
                defaultMessage="{n, plural, one {# uploaded file could not be removed again and remains stored.} other {# uploaded files could not be removed again and remain stored.}}"
                values={{ n: warning.blobCount }}
              />
            ) : null}
          </div>
          <p className="mt-1 text-smycles-label text-ink">
            <FormattedMessage
              id="service.page.bulkYaml.warningBlobCleanupHint"
              defaultMessage="An operator can remove them by hand using these storage ids."
            />
          </p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {warning.storageIds.map((id) => (
              <li key={id} className="font-mono text-smycles-micro text-ink-muted">
                {id}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
